import {
  NAME_MAX,
  RECIPE_INGREDIENT_MAX,
  RECIPE_MINUTES_MAX,
  RECIPE_NOTES_MAX,
  RECIPE_SERVINGS_MAX,
  RECIPE_STEP_MAX,
  RECIPE_TAG_MAX,
  type RecipeDto,
} from '@iac/contracts';
import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DeleteButton } from '../../components/DeleteButton';
import { Field, TextareaField } from '../../components/Field';
import { LineListEditor } from '../../components/LineListEditor';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { TagInput } from '../../components/TagInput';
import { useDict } from '../../i18n/dictionary';
import { useLocale } from '../../lib/locale';
import { usePageTitle } from '../../lib/page';
import {
  useCreateRecipe,
  useDeleteRecipe,
  useRecipes,
  useUpdateRecipe,
} from './api';
import { recipesDict } from './i18n';
import { useRecipeFromUrl } from './RecipeView';
import { tagsInUse } from './search';
import { NotFound } from '../../routes/NotFound';

/** What the form edits — numbers as the fields' text, as the body takes them. */
interface RecipeValues {
  name: string;
  servings: string;
  minutes: string;
  ingredients: string[];
  steps: string[];
  tags: string[];
  notes: string;
}

/** An empty list still shows one row, ready for typing. */
const atLeastOne = (lines: readonly string[]) =>
  lines.length > 0 ? [...lines] : [''];

function valuesOf(recipe: RecipeDto | null): RecipeValues {
  return {
    name: recipe?.name ?? '',
    servings: recipe?.servings?.toString() ?? '',
    minutes: recipe?.minutes?.toString() ?? '',
    ingredients: atLeastOne(recipe?.ingredients ?? []),
    steps: atLeastOne(recipe?.steps ?? []),
    tags: [...(recipe?.tags ?? [])],
    notes: recipe?.notes ?? '',
  };
}

export function RecipeNew() {
  const create = useCreateRecipe();
  const navigate = useNavigate();
  return (
    <RecipeForm
      recipe={null}
      pending={create.isPending}
      cancelTo="/recipes"
      onSubmit={(values) =>
        create.mutate(values, {
          onSuccess: (recipe) => void navigate(`/recipes/${recipe.id}`),
        })
      }
    />
  );
}

export function RecipeEdit() {
  const recipe = useRecipeFromUrl();
  const update = useUpdateRecipe();
  const navigate = useNavigate();
  if (recipe === undefined) return null;
  if (recipe === null) return <NotFound />;
  return (
    <RecipeForm
      key={recipe.id}
      recipe={recipe}
      pending={update.isPending}
      cancelTo={`/recipes/${recipe.id}`}
      onSubmit={(values) =>
        update.mutate(
          { id: recipe.id, ...values },
          { onSuccess: () => void navigate(`/recipes/${recipe.id}`) },
        )
      }
    />
  );
}

/**
 * One form for both pages. A failed save — a duplicate name, say — leaves
 * everything typed in place; the reason is toasted.
 */
function RecipeForm({
  recipe,
  pending,
  cancelTo,
  onSubmit,
}: {
  recipe: RecipeDto | null;
  pending: boolean;
  cancelTo: string;
  onSubmit: (values: RecipeValues) => void;
}) {
  const t = useDict(recipesDict);
  const title = recipe ? t.editRecipe : t.newRecipe;
  usePageTitle(`${title} — ${t.titleSuffix}`);
  const formId = useId();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { data: recipes } = useRecipes();
  const remove = useDeleteRecipe();
  const [values, setValues] = useState(() => valuesOf(recipe));
  const [tagDraft, setTagDraft] = useState('');
  const set = <K extends keyof RecipeValues>(key: K, value: RecipeValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const suggestions = useMemo(
    () => tagsInUse(recipes ?? [], new Intl.Collator(locale)),
    [recipes, locale],
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    // A tag typed but not added with Enter is still meant; the contract drops
    // it when blank and dedupes it.
    onSubmit({ ...values, tags: [...values.tags, tagDraft] });
  }

  const lineLabels = {
    moveUp: t.moveUp,
    moveDown: t.moveDown,
    remove: t.removeLine,
  };

  return (
    <>
      <PageHeader
        title={title}
        actions={
          recipe && (
            <DeleteButton
              label={t.deleteRecipe}
              confirm={t.confirmDelete(recipe.name)}
              onConfirm={() => {
                // Leave first: the edit page of a deleted recipe has nothing
                // to show. A failure is toasted on the list.
                remove.mutate(recipe.id);
                void navigate('/recipes');
              }}
            />
          )
        }
      />
      <Panel>
        <form onSubmit={submit} className="form-stack">
          <Field
            id={`${formId}-name`}
            label={t.name}
            required
            maxLength={NAME_MAX}
            autoComplete="off"
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
          />
          <div className="recipe-form-numbers">
            <Field
              id={`${formId}-servings`}
              label={t.servings}
              type="number"
              inputMode="numeric"
              min={1}
              max={RECIPE_SERVINGS_MAX}
              value={values.servings}
              onChange={(e) => set('servings', e.target.value)}
            />
            <Field
              id={`${formId}-minutes`}
              label={t.minutes}
              type="number"
              inputMode="numeric"
              min={1}
              max={RECIPE_MINUTES_MAX}
              value={values.minutes}
              onChange={(e) => set('minutes', e.target.value)}
            />
          </div>
          <LineListEditor
            label={t.ingredients}
            value={values.ingredients}
            onChange={(lines) => set('ingredients', lines)}
            maxLength={RECIPE_INGREDIENT_MAX}
            labels={{
              ...lineLabels,
              item: (i) => t.ingredient(i + 1),
              add: t.addIngredient,
            }}
          />
          <LineListEditor
            label={t.steps}
            value={values.steps}
            onChange={(lines) => set('steps', lines)}
            numbered
            maxLength={RECIPE_STEP_MAX}
            labels={{
              ...lineLabels,
              item: (i) => t.step(i + 1),
              add: t.addStep,
            }}
          />
          <TagInput
            id={`${formId}-tags`}
            label={t.tags}
            value={values.tags}
            onChange={(tags) => set('tags', tags)}
            draft={tagDraft}
            onDraftChange={setTagDraft}
            suggestions={suggestions}
            removeLabel={t.removeTag}
            maxLength={RECIPE_TAG_MAX}
          />
          <TextareaField
            id={`${formId}-notes`}
            label={t.notes}
            maxLength={RECIPE_NOTES_MAX}
            value={values.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
          <div className="form-actions">
            <Link to={cancelTo} className="btn-ghost">
              {t.cancel}
            </Link>
            <button
              type="submit"
              className="btn-primary recipe-form-save"
              disabled={pending || values.name.trim() === ''}
            >
              {pending ? t.saving : t.save}
            </button>
          </div>
        </form>
      </Panel>
    </>
  );
}
